import { useCallback, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import ProgramWizard from "../components/ProgramWizard";
import axios from "axios";
import { Button } from "../components/ui/button";
import { AlertCircle, Loader2 } from "lucide-react";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";

const EditProgramPage = ({ embedded = false, programId, routeBase = "/admin/programs" }) => {
  const { id: routeProgramId } = useParams();
  const id = programId || routeProgramId;
  const { token } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [editingProgram, setEditingProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!token || !id) return;
    try {
      setLoading(true);
      setError("");
      const headers = { Authorization: `Bearer ${token}` };
      const [orgsRes, programRes] = await Promise.all([
        axios.get(`${API}/organizations`, { headers }),
        axios.get(`${API}/programs/${id}`, { headers }),
      ]);
      setOrganizations(orgsRes.data || []);
      setEditingProgram(programRes.data);
    } catch (err) {
      console.error("Failed to fetch program data:", err);
      setError(err.response?.status === 404 ? "This program could not be found." : "The program editor could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSuccess = () => {
    trackAdminWorkflowCompleted("program_edit");
    navigate(`${routeBase}/${id}`);
  };

  if (loading) {
    const content = (
        <div className="flex min-h-[60vh] items-center justify-center bg-[#f3f7f2]">
          <div className="flex items-center gap-3 rounded-2xl border border-[#d8dfd9] bg-[#fbfcf8] px-5 py-4 text-[#66736f] shadow-sm">
            <Loader2 className="h-5 w-5 animate-spin text-[#16877c]" /> Loading program editor…
          </div>
        </div>
    );
    return embedded ? content : <AdminLayout>{content}</AdminLayout>;
  }

  if (error || !editingProgram) {
    const content = (
        <div className="flex min-h-[60vh] items-center justify-center bg-[#f3f7f2] px-4">
          <div role="alert" className="w-full max-w-lg rounded-[24px] border border-red-200 bg-[#fbfcf8] p-6 text-center shadow-sm">
            <AlertCircle className="mx-auto h-8 w-8 text-red-600" />
            <h1 className="mt-4 text-xl font-bold text-[#101a19]">Unable to open this program</h1>
            <p className="mt-2 text-sm text-[#66736f]">{error || "The program editor is unavailable."}</p>
            <div className="mt-5 flex justify-center gap-2">
              <Button variant="outline" onClick={() => navigate(routeBase)}>Back to Programs</Button>
              <Button onClick={fetchData} className="bg-[#16877c] text-white hover:bg-[#116d64]">Try again</Button>
            </div>
          </div>
        </div>
    );
    return embedded ? content : <AdminLayout>{content}</AdminLayout>;
  }

  const content = (
      <ProgramWizard
        mode="page"
        organizations={organizations}
        editingProgram={editingProgram}
        onSuccess={handleSuccess}
        onCancel={() => navigate(`${routeBase}/${id}`)}
        open={false}
        onOpenChange={() => {}}
      />
  );
  return embedded ? content : <AdminLayout>{content}</AdminLayout>;
};

export default EditProgramPage;
